import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiSave, FiPlus, FiX } from "react-icons/fi";
import CircularProgress from "@mui/material/CircularProgress";
import { remindersApi } from "./remindersApi";
import { showErrorToast, showSuccessToast } from "../../constants/ToastService";
import "./Reminders.css";

const EMPTY_FORM = {
    title: "",
    description: "",
    category: "",
    reference_number: "",
    start_date: "",
    expiry_date: "",
    grace_period_days: 0,
    amount: "",
    currency: "INR",
    provider_name: "",
    provider_contact: "",
    provider_email: "",
    provider_url: "",
    priority: "MEDIUM",
    recurrence: "NONE",
    custom_recurrence_days: "",
    alert_days_before: [30, 15, 7, 3, 1],
    notes: "",
    assigned_to: "",
};

const ReminderForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);

    const [form, setForm] = useState(EMPTY_FORM);
    const [categories, setCategories] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showCategoryForm, setShowCategoryForm] = useState(false);
    const [newCategory, setNewCategory] = useState({ name: "", color_code: "#7c5cfc" });

    const fetchCategories = useCallback(async () => {
        try {
            const res = await remindersApi.getCategories();
            if (res.status === "success") setCategories(res.data || []);
        } catch (err) { /* silent */ }
    }, []);

    const fetchEmployees = useCallback(async () => {
        try {
            const token = localStorage.getItem("authToken");
            const headers = { "Content-Type": "application/json" };
            if (token) headers.Authorization = `Bearer ${token}`;
            const API_BASE_URL = (await import("../RestAPI")).default;
            const res = await fetch(`${API_BASE_URL.replace(/\/$/, "")}/core/employee/`, { headers });
            const data = await res.json();
            if (data.status === "success") setEmployees(data.data || []);
        } catch (err) { /* silent */ }
    }, []);

    const fetchItem = useCallback(async () => {
        if (!id) return;
        try {
            setLoading(true);
            const res = await remindersApi.getItem(id);
            if (res.status === "success" && res.data) {
                const d = res.data;
                setForm({
                    title: d.title || "",
                    description: d.description || "",
                    category: d.category || "",
                    reference_number: d.reference_number || "",
                    start_date: d.start_date || "",
                    expiry_date: d.expiry_date || "",
                    grace_period_days: d.grace_period_days || 0,
                    amount: d.amount || "",
                    currency: d.currency || "INR",
                    provider_name: d.provider_name || "",
                    provider_contact: d.provider_contact || "",
                    provider_email: d.provider_email || "",
                    provider_url: d.provider_url || "",
                    priority: d.priority || "MEDIUM",
                    recurrence: d.recurrence || "NONE",
                    custom_recurrence_days: d.custom_recurrence_days || "",
                    alert_days_before: d.alert_days_before || [30, 15, 7, 3, 1],
                    notes: d.notes || "",
                    assigned_to: d.assigned_to || "",
                });
            }
        } catch (err) {
            showErrorToast(err.message);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { fetchCategories(); fetchEmployees(); }, [fetchCategories, fetchEmployees]);
    useEffect(() => { fetchItem(); }, [fetchItem]);

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.title || !form.category || !form.start_date || !form.expiry_date) {
            showErrorToast("Please fill in all required fields");
            return;
        }

        try {
            setSaving(true);
            const payload = { ...form };
            if (payload.amount === "") payload.amount = null;
            if (payload.assigned_to === "") payload.assigned_to = null;
            if (payload.custom_recurrence_days === "") payload.custom_recurrence_days = null;

            let res;
            if (isEdit) {
                payload.id = id;
                res = await remindersApi.updateItem(payload);
            } else {
                res = await remindersApi.createItem(payload);
            }

            if (res.status === "success") {
                showSuccessToast(isEdit ? "Reminder updated" : "Reminder created");
                navigate(isEdit ? `/reminders/${id}` : "/reminders/list");
            }
        } catch (err) {
            showErrorToast(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleCreateCategory = async () => {
        if (!newCategory.name) { showErrorToast("Category name is required"); return; }
        try {
            const res = await remindersApi.createCategory(newCategory);
            if (res.status === "success") {
                showSuccessToast("Category created");
                setShowCategoryForm(false);
                setNewCategory({ name: "", color_code: "#7c5cfc" });
                fetchCategories();
                if (res.data?.id) handleChange("category", res.data.id);
            }
        } catch (err) {
            showErrorToast(err.message);
        }
    };

    if (loading) {
        return <div className="rem-loading"><CircularProgress size={40} sx={{ color: "var(--accent)" }} /></div>;
    }

    return (
        <div className="rem-page">
            <div className="rem-header">
                <div className="rem-header__left">
                    <button className="rem-btn rem-btn--ghost" onClick={() => navigate(-1)}>
                        <FiArrowLeft size={16} />
                    </button>
                    <h1 className="rem-title">{isEdit ? "Edit Reminder" : "New Reminder"}</h1>
                </div>
            </div>

            <form className="rem-form" onSubmit={handleSubmit}>
                {/* Basic Info */}
                <div className="rem-form__section">
                    <h3 className="rem-form__section-title">Basic Information</h3>
                    <div className="rem-form__grid">
                        <div className="rem-form__field rem-form__field--full">
                            <label>Title <span className="rem-required">*</span></label>
                            <input type="text" value={form.title} onChange={(e) => handleChange("title", e.target.value)} placeholder="e.g., AWS Subscription, SSL Certificate, Office Rent" className="rem-input" />
                        </div>
                        <div className="rem-form__field">
                            <label>Category <span className="rem-required">*</span></label>
                            <div className="rem-form__cat-row">
                                <select value={form.category} onChange={(e) => handleChange("category", e.target.value)} className="rem-select">
                                    <option value="">Select Category</option>
                                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                                <button type="button" className="rem-btn rem-btn--icon" onClick={() => setShowCategoryForm(!showCategoryForm)} title="Add Category">
                                    <FiPlus size={16} />
                                </button>
                            </div>
                            {showCategoryForm && (
                                <div className="rem-inline-form">
                                    <input type="text" placeholder="Category name" value={newCategory.name} onChange={(e) => setNewCategory((c) => ({ ...c, name: e.target.value }))} className="rem-input" />
                                    <input type="color" value={newCategory.color_code} onChange={(e) => setNewCategory((c) => ({ ...c, color_code: e.target.value }))} className="rem-color-picker" />
                                    <button type="button" className="rem-btn rem-btn--sm rem-btn--primary" onClick={handleCreateCategory}>Add</button>
                                    <button type="button" className="rem-btn rem-btn--sm rem-btn--ghost" onClick={() => setShowCategoryForm(false)}><FiX size={14} /></button>
                                </div>
                            )}
                        </div>
                        <div className="rem-form__field">
                            <label>Priority</label>
                            <select value={form.priority} onChange={(e) => handleChange("priority", e.target.value)} className="rem-select">
                                <option value="CRITICAL">Critical</option>
                                <option value="HIGH">High</option>
                                <option value="MEDIUM">Medium</option>
                                <option value="LOW">Low</option>
                            </select>
                        </div>
                        <div className="rem-form__field rem-form__field--full">
                            <label>Description</label>
                            <textarea value={form.description} onChange={(e) => handleChange("description", e.target.value)} placeholder="Additional details about this reminder" className="rem-textarea" rows={3} />
                        </div>
                        <div className="rem-form__field">
                            <label>Reference / Invoice Number</label>
                            <input type="text" value={form.reference_number} onChange={(e) => handleChange("reference_number", e.target.value)} placeholder="e.g., INV-2026-001" className="rem-input" />
                        </div>
                        <div className="rem-form__field">
                            <label>Assigned To</label>
                            <select value={form.assigned_to} onChange={(e) => handleChange("assigned_to", e.target.value)} className="rem-select">
                                <option value="">None</option>
                                {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Dates & Recurrence */}
                <div className="rem-form__section">
                    <h3 className="rem-form__section-title">Dates & Recurrence</h3>
                    <div className="rem-form__grid">
                        <div className="rem-form__field">
                            <label>Start Date <span className="rem-required">*</span></label>
                            <input type="date" value={form.start_date} onChange={(e) => handleChange("start_date", e.target.value)} className="rem-input" />
                        </div>
                        <div className="rem-form__field">
                            <label>Expiry Date <span className="rem-required">*</span></label>
                            <input type="date" value={form.expiry_date} onChange={(e) => handleChange("expiry_date", e.target.value)} className="rem-input" />
                        </div>
                        <div className="rem-form__field">
                            <label>Grace Period (Days)</label>
                            <input type="number" value={form.grace_period_days} onChange={(e) => handleChange("grace_period_days", parseInt(e.target.value) || 0)} className="rem-input" min="0" />
                        </div>
                        <div className="rem-form__field">
                            <label>Recurrence</label>
                            <select value={form.recurrence} onChange={(e) => handleChange("recurrence", e.target.value)} className="rem-select">
                                <option value="NONE">None (One-time)</option>
                                <option value="MONTHLY">Monthly</option>
                                <option value="QUARTERLY">Quarterly</option>
                                <option value="HALF_YEARLY">Half Yearly</option>
                                <option value="YEARLY">Yearly</option>
                                <option value="CUSTOM">Custom</option>
                            </select>
                        </div>
                        {form.recurrence === "CUSTOM" && (
                            <div className="rem-form__field">
                                <label>Custom Recurrence (Days)</label>
                                <input type="number" value={form.custom_recurrence_days} onChange={(e) => handleChange("custom_recurrence_days", e.target.value)} className="rem-input" min="1" />
                            </div>
                        )}
                    </div>
                </div>

                {/* Cost & Provider */}
                <div className="rem-form__section">
                    <h3 className="rem-form__section-title">Cost & Provider</h3>
                    <div className="rem-form__grid">
                        <div className="rem-form__field">
                            <label>Amount</label>
                            <input type="number" value={form.amount} onChange={(e) => handleChange("amount", e.target.value)} placeholder="0.00" className="rem-input" step="0.01" min="0" />
                        </div>
                        <div className="rem-form__field">
                            <label>Currency</label>
                            <select value={form.currency} onChange={(e) => handleChange("currency", e.target.value)} className="rem-select">
                                <option value="INR">INR</option>
                                <option value="USD">USD</option>
                                <option value="EUR">EUR</option>
                                <option value="GBP">GBP</option>
                            </select>
                        </div>
                        <div className="rem-form__field">
                            <label>Provider / Vendor Name</label>
                            <input type="text" value={form.provider_name} onChange={(e) => handleChange("provider_name", e.target.value)} placeholder="e.g., Amazon AWS" className="rem-input" />
                        </div>
                        <div className="rem-form__field">
                            <label>Provider Contact</label>
                            <input type="text" value={form.provider_contact} onChange={(e) => handleChange("provider_contact", e.target.value)} placeholder="Phone or contact person" className="rem-input" />
                        </div>
                        <div className="rem-form__field">
                            <label>Provider Email</label>
                            <input type="email" value={form.provider_email} onChange={(e) => handleChange("provider_email", e.target.value)} placeholder="support@provider.com" className="rem-input" />
                        </div>
                        <div className="rem-form__field">
                            <label>Provider URL</label>
                            <input type="url" value={form.provider_url} onChange={(e) => handleChange("provider_url", e.target.value)} placeholder="https://..." className="rem-input" />
                        </div>
                    </div>
                </div>

                {/* Notes */}
                <div className="rem-form__section">
                    <h3 className="rem-form__section-title">Notes</h3>
                    <div className="rem-form__grid">
                        <div className="rem-form__field rem-form__field--full">
                            <textarea value={form.notes} onChange={(e) => handleChange("notes", e.target.value)} placeholder="Any additional notes, login credentials reference, account details..." className="rem-textarea" rows={4} />
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="rem-form__actions">
                    <button type="button" className="rem-btn rem-btn--outline" onClick={() => navigate(-1)}>Cancel</button>
                    <button type="submit" className="rem-btn rem-btn--primary" disabled={saving}>
                        {saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <><FiSave size={14} /> {isEdit ? "Update" : "Create"} Reminder</>}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ReminderForm;
